import { WizDateUtils, WizMathUtils } from '@wizeoo/akm-core-lib';
import {
  EMPackEntry,
  EntityModification,
  EntityModificationPack,
  TranslatedEmb,
  TranslatedKeyPair
} from '@wizeoo/akm-message-lib';
import { IAmUnique, cloneEM } from './ivo';
import { Model } from '../../model/model';
import { EmVO } from '../../vo/em/em.vo';
import { getEntityClassFromVO } from './entity-class';
import { EMMaps, EM_ACTION, IVo } from '@wizeoo/akm-compute-lib';


/**
 * La valeur max pour les champs old_value et new_value
 */
const VALUE_MAX_LENGTH = 255;

/**
 * gestion des EntityModifications :
 * suivi de TOUS les create/update pour sauvegarde silencieuse
 */
export class EMEngine {

  // lien vers le modèle
  public static model: Model = null;

  /**
   * création d'un nouveau pack de modifications correctement instancié (domain, portée...)
   * @param clone l'objet en cours d'altération
   * le clone doit porter les informations de domain / bu / source et UID (IVo)
   */
  public static newEMPack(clone: IVo): EntityModificationPack {
    const emp: EntityModificationPack = new EntityModificationPack();
    emp.packUID = WizMathUtils.createUUID();
    emp.domain = clone.domain;
    emp.businessUnitCode = clone.businessUnitCode;
    emp.emTime = WizDateUtils.currentTimestamp;
    emp.entries = [];
    emp.ems = [];
    return emp;
  }

  /**
   * création d'un EMPackEntry
   * @param clone le VO contenant l'objet métier
   * @param action l'action CUD en cours
   * @param emPack le pack de destination
   */
  public static newPackEntry(clone: IVo, action: EM_ACTION, emPack: EntityModificationPack): EMPackEntry {
    const em: EMPackEntry = new EMPackEntry();
    em.domain = emPack.domain;
    em.businessUnitCode = emPack.businessUnitCode;
    em.entityClass = EMMaps.getRange(clone.constructor)[0];
    em.entity = clone.source;
    switch (action) {
      case EM_ACTION.CREATE:
        em.entity.createBy = this.model.connectedUser.mail;
        break;
      case EM_ACTION.UPDATE:
        em.entity.updateBy = this.model.connectedUser.mail;
        break;
    }
    em.entityUID = clone.uid;
    em.operation = action;
    em.packUID = emPack.packUID;
    const entrySet: Set<string> = new Set(emPack.entries.map((e: EMPackEntry) => e.entityUID));
    if (!entrySet.has(em.entityUID)) {
      emPack.entries.push(em);
    } else {
      // double update sur même objet, pas grave console.log('uid already', clone);
    }
    return em;
  }

  /**
   * création d'une EntityModification
   * @param clone le VO contenant l'objet métier
   * @param action l'action CUD en cours
   * @param emPack le pack de destination
   * @param path le nom du champ
   * @param oldValue valeur de la source
   * @param newValue valeur du clone
   */
  public static newEM(
    myClone: IVo,
    action: EM_ACTION,
    emPack: EntityModificationPack,
    path: string = null,
    oldValue: any = null,
    newValue: any = null
  ): EntityModification {
    const em: EntityModification = new EntityModification();
    em.domain = emPack.domain;
    em.businessUnitCode = emPack.businessUnitCode;
    em.visionCode = this.model.visionCode;
    em.packUID = emPack.packUID;
    em.uid = WizMathUtils.createUUID();
    em.entityClass = getEntityClassFromVO(myClone);
    em.entityId = myClone.id;
    em.entityUID = myClone.uid;
    em.operation = action;
    em.range = EMMaps.getRange(myClone.constructor)[1];
    em.login = this.model.connectedUser.mail;
    em.refCode = myClone.refCode || null;

    // attention à null / undefined
    em.path = path;
    // em.oldValue = this.controlCheckValue(oldValue);
    // em.newValue = this.controlCheckValue(newValue);
    if(action === EM_ACTION.DELETE) {
      em.oldValueJson = cloneEM(myClone, em.entityClass);
    } else if(action === EM_ACTION.CREATE) {
      em.newValueJson = cloneEM(myClone, em.entityClass);
    } else {
      // console.log('em', oldValue, newValue);
      if(!!oldValue) {
        try {
          em.oldValueJson = JSON.parse('{"oldvalue":"' + oldValue + '"}');
        } catch(e:any) {
          const sf = JSON.stringify(oldValue);
          // console.log('sf', sf, dummyString);
          em.oldValueJson = JSON.parse('{"oldvalue":' + sf + '}');
        }
        
      }
      if(!!newValue) {
        try {
          em.newValueJson = JSON.parse('{"newValue":"' + newValue + '"}');
        } catch(e:any) {       
          const sf = JSON.stringify(newValue);
          // console.log('sf', sf, dummyString);
          em.newValueJson = JSON.parse('{"newValue":' + sf + '}');
        }
      }
    }
    
    // console.log('em', em.oldValueJson, em.newValueJson);
    emPack.ems.push(em);
    return em;
  }

  /**
   * S'assure que la valeur est correcte pour insertion en BDD (=> qu'on ne dépasse pas la taille max)
   */
  // private static controlCheckValue(value: any): any {
  //   // pas une chaine string? pas de check.
  //   if (!value || typeof value !== 'string') {
  //     return value;
  //   }

  //   return value.substring(0, VALUE_MAX_LENGTH);
  // }

  /**
   * comparaison du clone avec l'objet source pour génération des EMs
   * TODO gestion en mode collection
   * @param clone : l'objet altéré
   * @param sourceVO : l'objet source inaltéré
   * @param referenceCode: facultatif, code d'unicité de l'objet "porteur" (pas forcément la classe de l'entity)
   * @param referenceFied: facultatif, code du champ sur l'objet porteur
   * @return EntityModificationPack
   */
  public static mergeEM(
    clone: IVo,
    sourceVO: any,
    referenceCode?: string,
    referenceField?: string
  ): EntityModificationPack {
    const emPack: EntityModificationPack = this.newEMPack(clone);
    this.addMergeEM(clone, sourceVO, emPack);
    if (emPack && referenceCode && referenceField) {
      emPack.ems.forEach((em: EntityModification) => {
        em.refCode = referenceCode;
        em.refField = referenceField;
      });
    }
    return emPack;
  }

  /**
   * comparaison du clone avec l'objet source pour génération des EMs et ajout dans le pack (créé si non fourni)
   * TODO gestion en mode collection
   * @param clone : l'objet altéré
   * @param sourceVO : l'objet source inaltéré
   * @param emPack le pack en cours d'alimentation
   * @return EntityModificationPack
   */
  public static addMergeEM(clone: IVo, sourceVO: any, emPack: EntityModificationPack): EntityModificationPack {
    if (!emPack) {
      emPack = this.newEMPack(clone);
    }
    this.compare(clone, sourceVO, emPack); // alimentation des EMs

    if (emPack.ems.length > 0) {
      // à minima une modification, sinon.. rien
      this.newPackEntry(clone, EM_ACTION.UPDATE, emPack); // alimentation du packEntry
    } else {
      emPack = null;
    }
    return emPack;
  }

  /**
   * création d'un EM de création d'objet
   * @param clone : l'objet en proposition de création
   * @return EntityModificationPack
   */
  public static createEM(clone: IVo): EntityModificationPack {
    const emPack: EntityModificationPack = this.newEMPack(clone);
    this.addCreationEM(clone, emPack);
    return emPack;
  }

  /**
   * Ajout d'un EM de création d'objet à un pack (créé si nécessaire)
   * @param clone : l'objet en proposition de création
   * @param emPack le pack en cours d'alimentation
   * @return EntityModificationPack
   */
  public static addCreationEM(clone: IVo, emPack: EntityModificationPack): EntityModificationPack {
    if (clone) {
      if (!emPack) {
        emPack = this.newEMPack(clone);
      }
      this.newEM(clone, EM_ACTION.CREATE, emPack); // alimentation de l'EM
      this.newPackEntry(clone, EM_ACTION.CREATE, emPack); // alimentation du packEntry
    }
    return emPack;
  }

  /**
   * Ajout d'un EM de suppression d'objet à un pack (créé si nécessaire)
   * @param clone : l'objet en proposition de suppression
   * @return EntityModificationPack
   */
  public static removeEM(clone: IVo): EntityModificationPack {
    const emPack: EntityModificationPack = this.newEMPack(clone);
    this.addRemoveEM(clone, emPack);
    return emPack;
  }

  /**
   * création d'un EM de suppression d'objet
   * @param clone : l'objet en proposition de suppression
   * @return EntityModificationPack
   */
  public static addRemoveEM(clone: IVo, emPack: EntityModificationPack): EntityModificationPack {
    if (!emPack) {
      emPack = this.newEMPack(clone);
    }
    this.newEM(clone, EM_ACTION.DELETE, emPack); // alimentation de l'EM
    this.newPackEntry(clone, EM_ACTION.DELETE, emPack); // alimentation du packEntry
    return emPack;
  }

  /**
   * comparaison brute de 2 objets
   * @param to : l'objet en proposition de création
   * @param from : l'objet source inaltéré
   * @param le pack en cours d'alimentation
   * @return entityModification[]
   */
  public static compare(to: any, from: any, emPack: EntityModificationPack): EntityModification[] {
    // collecte de la map de champs à contrôler
    const compareMap: Map<string, [string, boolean, boolean]> = EMMaps.getFieldsMap(to.constructor);
    compareMap.forEach((value: [string, boolean, boolean], path: string) => {
      const toVal: any = to[path];
      const fromVal: any = from[path];

      if (value[1]) {
        // c'est une collection
        this.checkSubCollection(to, path, toVal, fromVal, emPack);
      } else if(value[2]) {
        // c'est un objet de type keyPairs
        this.checkKeyPairs(to, path, toVal, fromVal, emPack);
      }
      else {
        // type primitif objet complexe (translationLabel, wizByte)
        if (value[0] === 'TranslatedEmb') {
          // gestion dédié des changements de trad
          this.checkTranslatable(to, path, toVal, fromVal, emPack);
        } else if (value[0] === 'binary') {
          // gestion des binaires
          if (toVal !== fromVal) {
            this.newEM(to, EM_ACTION.UPDATE, emPack, path, 'binary', 'binary');
          }
        } else {
          // type primitif
          if (toVal !== fromVal) {
            this.newEM(to, EM_ACTION.UPDATE, emPack, path, fromVal, toVal);
          }
        }
      }
    });
    return emPack.ems;
  }

  /**
   * identification du delta sur un label traductible Alimente les EMs du pack
   * @param to le clone modifié
   * @param path le champ TranslatedEmb
   * @param toVal la valeur cible
   * @param fromVal la valeur d'origine
   * @param emPack le pack en cours de création
   */
  private static checkTranslatable(to: any, path: string, toVal: TranslatedEmb, fromVal: TranslatedEmb, emPack: EntityModificationPack): void {

    // on va comparer toutes les entrées (couple CODE LANGUE|TranslatableLabelEntry)
    const existLang: Map<string, TranslatedKeyPair> = new Map<string, TranslatedKeyPair>();
    if (fromVal) {
      if (!fromVal.keys) { // mal initialisé...
        fromVal.keys = {};
      }
      Object.values(fromVal.keys).map((entry: TranslatedKeyPair) => {
        existLang.set(entry.cod, entry);
      });
    }
    // 2 cas possibles: update ou ajout. Pas de delete d'une entrée de trad (au pire, chaine vide)
    if (toVal) {
      const toCheck: TranslatedKeyPair[] = Object.values(toVal.keys);
      toCheck.forEach((entry: TranslatedKeyPair) => {
        if (existLang.has(entry.cod)) { // existait déjà
          if (entry.val !== existLang.get(entry.cod).val) { // delta ?
            this.newEM(to, EM_ACTION.UPDATE, emPack, path + '.' + entry.cod, existLang.get(entry.cod).val, entry.val);
          }
        } else { // création
          this.newEM(to, EM_ACTION.UPDATE, emPack, path + '.' + entry.cod, null, entry.val);
        }
      });
    }
  }

  /**
   * identification du delta sur une sous-collection
   * Les sous-objets portent TOUS un UID
   * @param to le clone modifié
   * @param path le champ contenant la collection
   * @param toVal l'objet cible
   * @param fromVal l'objet d'origine
   * @param emPack le pack en cours de création
   */
  private static checkKeyPairs(to: any, path: string, toVal: {}, fromVal: {}, emPack: EntityModificationPack): void {

      // la map des existants
      const existMap: Map<string, IAmUnique> = new Map<string, IAmUnique>();
      if (fromVal) {
        Object.keys(fromVal).forEach((key: string) => {
          existMap.set(key, fromVal[key]);
        });
      }
      // la map du clone
      const cloneMap: Map<string, any> = new Map<string, any>();
      if (toVal) {
        Object.keys(toVal).forEach((key: string) => {
          existMap.set(key, toVal[key]);
        });
      }

      // extraction des morts
      const deads: string[] = Array.from(existMap.keys()).filter((item) => !cloneMap.has(item));
      // extraction des naissances
      const borns: string[] = Array.from(cloneMap.keys()).filter((item) => !existMap.has(item));

      deads.forEach((dead) => {
        this.newEM(to, EM_ACTION.UPDATE, emPack, path, dead, null);
      });
      borns.forEach((born) => {
        this.newEM(to, EM_ACTION.UPDATE, emPack, path, null, born);
      });

    }


  /**
   * identification du delta sur une sous-collection
   * Les sous-objets portent TOUS un UID
   * @param to le clone modifié
   * @param path le champ contenant la collection
   * @param toVal la collection cible
   * @param fromVal la collection d'origine
   * @param emPack le pack en cours de création
   */
  private static checkSubCollection(
    to: any,
    path: string,
    toVal: IAmUnique[],
    fromVal: IAmUnique[],
    emPack: EntityModificationPack
  ): void {
    // la map des existants
    const existMap: Map<string, IAmUnique> = new Map<string, IAmUnique>();
    if (fromVal) {
      fromVal.map((item: IAmUnique) => {
        existMap.set(item.uid, item);
      });
    }
    // la map du clone
    const cloneMap: Map<string, any> = new Map<string, any>();
    if (toVal) {
      toVal.map((item: IAmUnique) => {
        cloneMap.set(item.uid, item);
      });
    }

    // extraction des morts
    const deads: string[] = Array.from(existMap.keys()).filter((item) => !cloneMap.has(item));
    // extraction des naissances
    const borns: string[] = Array.from(cloneMap.keys()).filter((item) => !existMap.has(item));

    deads.forEach((dead) => {
      this.newEM(to, EM_ACTION.UPDATE, emPack, path, dead, null);
    });
    borns.forEach((born) => {
      this.newEM(to, EM_ACTION.UPDATE, emPack, path, null, born);
    });
  }

  /**
   * encapsulation des EMs sur retour de service pour affichage
   * @param pack le retour de service commit
   * @return des EM encapsulés en VO
   */
  public static generateEMVO(pack: EntityModificationPack): EmVO[] {
    const result: EmVO[] = [];
    pack.ems.forEach((em: EntityModification) => {
      const emvo: EmVO = new EmVO(em);
      result.push(emvo);
    });
    return result;
  }
}
